import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import UserTypePicker from "@/components/app/UserTypePicker";
import { userTypeLabel } from "@/lib/userTypes";
import { UserCog, Edit2 } from "lucide-react";

/**
 * Read-only summary that flips into an editor on click. Same component is
 * embedded in Vet & Health and Settings so the user has two natural places
 * to find this. Edits go through AuthContext.updateProfile so the live `user`
 * object stays in sync everywhere.
 */
export default function UserTypesEditCard({ overline = "Your profile" }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);

  const current = user?.user_types || [];
  const summary = current.length === 0
    ? "You haven't told us yet."
    : current.map(userTypeLabel).join(" · ");

  return (
    <Card className="border-border shadow-none rounded-lg" data-testid="user-types-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-10 w-10 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
            <UserCog size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">{overline}</p>
            <p className="font-display text-lg font-bold tracking-tight mt-0.5">Who you are on Your Dog Days</p>
            <p className="text-sm text-muted-foreground mt-1.5 break-words" data-testid="user-types-summary">{summary}</p>
            <p className="text-xs text-muted-foreground mt-1.5">
              Pick all that apply. We&apos;ll never lock features behind your answer &mdash; this is just so we can serve you better content over time.
            </p>
          </div>
          {!editing && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
              className="shrink-0"
              data-testid="user-types-edit"
            >
              <Edit2 size={13} className="mr-1.5" /> {current.length === 0 ? "Set it" : "Edit"}
            </Button>
          )}
        </div>

        {editing && (
          <div className="mt-5 pt-5 border-t border-border">
            <UserTypePicker
              initial={current}
              saveLabel="Save"
              showSecondary
              secondaryLabel="Cancel"
              onSecondary={() => setEditing(false)}
              onSaved={() => setEditing(false)}
              data_testid="user-types-edit-picker"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
